import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Plus, ArrowLeft, Save, Trash2, Wand2, Database, ExternalLink, Copy, Bot, Users, HelpCircle, Image as ImageIcon } from "lucide-react";
import { BrandLogo } from "@/components/Navbar";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { aiListAgents, aiGetAgent, aiSaveAgent, aiDeleteAgent, aiIngest, aiAutoConfig, aiInsights, aiAgentPhotos } from "@/lib/api";
import { toast } from "sonner";

const ORIGIN = process.env.REACT_APP_CANONICAL_ORIGIN || (typeof window !== "undefined" ? window.location.origin : "https://websitetech.pro");
const EMPTY = { org_id: "websitetech", name: "", display_name: "", site: "", industry: "", system_prompt: "", welcome_message: "Hi! How can I help you today?", suggestions: [], personality: "Friendly", languages: ["English"], primary_color: "#2563EB", provider: "openai", model: "gpt-5.4", web_research: true, vision: false, human_handoff: true, status: "active" };
const fieldCls = "w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-brand";
const MODELS = [["openai", "gpt-5.4"], ["anthropic", "claude-sonnet-4-6"], ["gemini", "gemini-3-flash-preview"]];

export default function AiConsole() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [agents, setAgents] = useState(null);
  const [form, setForm] = useState(null);
  const [insights, setInsights] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState("");
  const [ingestUrl, setIngestUrl] = useState("");
  const [autoUrl, setAutoUrl] = useState("");

  useEffect(() => { if (user === null || (user && user.role !== "admin")) navigate("/login"); }, [user, navigate]);
  const load = () => aiListAgents().then((d) => setAgents(d.agents)).catch(() => setAgents([]));
  useEffect(() => { load(); }, []);

  if (user === undefined || user === null || user.role !== "admin") return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-brand" size={26} /></div>;

  const openAgent = async (id) => {
    const a = await aiGetAgent(id);
    setForm(a); setInsights(null); setPhotos([]);
    aiInsights(id).then(setInsights).catch(() => {});
    aiAgentPhotos(id).then((d) => setPhotos(d.photos || [])).catch(() => {});
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy("save");
    try {
      const payload = { ...form, suggestions: Array.isArray(form.suggestions) ? form.suggestions : String(form.suggestions).split("\n").map((s) => s.trim()).filter(Boolean) };
      const saved = await aiSaveAgent(payload);
      setForm(saved); await load();
      if (saved?.id) aiInsights(saved.id).then(setInsights).catch(() => {});
      toast.success("Agent saved");
    } catch { toast.error("Save failed"); } finally { setBusy(""); }
  };
  const del = async () => {
    if (!form.id || !window.confirm("Delete this agent?")) return;
    await aiDeleteAgent(form.id); setForm(null); load(); toast.success("Deleted");
  };
  const runAuto = async () => {
    if (!autoUrl) return; setBusy("auto");
    try {
      const c = await aiAutoConfig(autoUrl);
      setForm((f) => ({ ...f, name: f.name || c.name, display_name: f.display_name || c.name, industry: c.industry, welcome_message: c.welcome_message, suggestions: c.suggestions, system_prompt: c.system_prompt, site: f.site || autoUrl.replace(/^https?:\/\//, "") }));
      toast.success("Draft configured from website");
    } catch { toast.error("Could not analyze that URL"); } finally { setBusy(""); }
  };
  const runIngest = async () => {
    if (!form?.id) { toast.error("Save the agent first"); return; }
    if (!ingestUrl) return; setBusy("ingest");
    try {
      const r = await aiIngest(form.id, ingestUrl);
      toast.success(`Ingested ${r.pages_ingested} page(s), ${r.chunks} chunks`);
      setForm((f) => ({ ...f, knowledge_count: r.knowledge_count })); setIngestUrl(""); await load();
    } catch { toast.error("Ingest failed"); } finally { setBusy(""); }
  };

  const assistantLink = form?.public_key ? `${ORIGIN}/assistant/${form.public_key}` : "";
  const embed = form?.public_key ? `<iframe src="${assistantLink}" title="${form.display_name || form.name}" style="border:0;position:fixed;bottom:20px;right:20px;width:400px;height:600px;max-width:calc(100vw - 40px);box-shadow:0 12px 40px rgba(0,0,0,.18);border-radius:16px;z-index:99999"></iframe>` : "";
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between sticky top-0 z-30">
        <Link to="/"><BrandLogo size={34} /></Link>
        <div className="flex items-center gap-4">
          <Link to="/admin" className="text-sm text-slate-500 hover:text-brand">Console</Link>
          <span className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><Bot size={15} /> AI Agents</span>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">
        {/* List */}
        <aside>
          <button onClick={() => { setForm({ ...EMPTY }); setInsights(null); }} data-testid="new-agent-btn" className="btn-primary rounded-lg w-full py-2.5 text-sm flex items-center justify-center gap-2 mb-4"><Plus size={16} /> New Agent</button>
          {agents === null ? <Loader2 className="animate-spin text-brand mx-auto mt-6" /> : (
            <div className="space-y-2">
              {agents.map((a) => (
                <button key={a.id} onClick={() => openAgent(a.id)} data-testid={`agent-row-${a.id}`} className={`w-full text-left bg-white border rounded-xl p-3 hover:border-brand transition-colors ${form?.id === a.id ? "border-brand" : "border-slate-200"}`}>
                  <p className="font-semibold text-slate-800 text-sm">{a.display_name || a.name}</p>
                  <p className="text-[11px] text-slate-400">{a.site || a.org_id} · {a.knowledge_count || 0} chunks</p>
                </button>
              ))}
              {agents.length === 0 && <p className="text-sm text-slate-400 text-center mt-6">No agents yet.</p>}
            </div>
          )}
        </aside>

        {/* Editor */}
        <section>
          {!form ? (
            <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-400">Select an agent or create a new one.</div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-xl font-bold text-slate-900">{form.id ? "Edit Agent" : "New Agent"}</h2>
                <div className="flex gap-2">
                  {form.id && <button onClick={del} className="btn-outline rounded-lg px-3 py-2 text-sm text-red-600"><Trash2 size={15} /></button>}
                  <button onClick={save} disabled={busy === "save"} data-testid="save-agent-btn" className="btn-primary rounded-lg px-4 py-2 text-sm flex items-center gap-2">{busy === "save" ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save</button>
                </div>
              </div>

              {/* Auto-config */}
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                <p className="text-sm font-semibold text-slate-700 flex items-center gap-1.5 mb-2"><Wand2 size={15} className="text-brand" /> Auto-configure from a website</p>
                <div className="flex gap-2">
                  <input value={autoUrl} onChange={(e) => setAutoUrl(e.target.value)} placeholder="https://client-site.com" className={fieldCls} data-testid="auto-url-input" />
                  <button onClick={runAuto} disabled={busy === "auto"} data-testid="auto-config-btn" className="btn-primary rounded-lg px-4 text-sm whitespace-nowrap flex items-center gap-1.5">{busy === "auto" ? <Loader2 size={14} className="animate-spin" /> : "Analyze"}</button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className="text-xs font-semibold text-slate-500">Name</label><input value={form.name} onChange={(e) => set("name", e.target.value)} className={fieldCls} data-testid="agent-name-input" /></div>
                <div><label className="text-xs font-semibold text-slate-500">Site / Business</label><input value={form.site || ""} onChange={(e) => set("site", e.target.value)} className={fieldCls} /></div>
                <div><label className="text-xs font-semibold text-slate-500">Personality</label>
                  <Select value={form.personality} onValueChange={(v) => set("personality", v)}>
                    <SelectTrigger className={fieldCls} data-testid="agent-personality-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{["Professional", "Friendly", "Sales-oriented", "Technical", "Concierge", "Educational"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><label className="text-xs font-semibold text-slate-500">Model</label>
                  <Select value={`${form.provider}/${form.model}`} onValueChange={(v) => { const [pv, md] = v.split("/"); set("provider", pv); set("model", md); }}>
                    <SelectTrigger className={fieldCls} data-testid="agent-model-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{MODELS.map(([pv, md]) => <SelectItem key={md} value={`${pv}/${md}`}>{pv} · {md}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><label className="text-xs font-semibold text-slate-500">Primary color</label>
                  <div className="flex items-center gap-2 mt-0.5">
                    <input type="color" value={form.primary_color} onChange={(e) => set("primary_color", e.target.value)} className="w-12 h-10 rounded-lg border border-slate-200 cursor-pointer p-0.5" />
                    <span className="text-sm font-mono text-slate-500">{form.primary_color}</span>
                  </div>
                </div>
                <div className="flex items-end gap-4">
                  <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={form.web_research} onChange={(e) => set("web_research", e.target.checked)} data-testid="web-research-toggle" /> Live web research</label>
                  <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={!!form.vision} onChange={(e) => set("vision", e.target.checked)} data-testid="vision-toggle" /> Photo / vision</label>
                  <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={form.human_handoff} onChange={(e) => set("human_handoff", e.target.checked)} /> Human handoff</label>
                </div>
              </div>
              <div><label className="text-xs font-semibold text-slate-500">Welcome message</label><input value={form.welcome_message} onChange={(e) => set("welcome_message", e.target.value)} className={fieldCls} /></div>
              <div><label className="text-xs font-semibold text-slate-500">System prompt</label><textarea value={form.system_prompt} onChange={(e) => set("system_prompt", e.target.value)} rows={4} className={fieldCls} /></div>
              <div><label className="text-xs font-semibold text-slate-500">Suggested questions (one per line)</label><textarea value={Array.isArray(form.suggestions) ? form.suggestions.join("\n") : form.suggestions} onChange={(e) => set("suggestions", e.target.value.split("\n"))} rows={3} className={fieldCls} /></div>

              {form.id && (
                <>
                  {/* Knowledge ingest */}
                  <div className="bg-white border border-slate-200 rounded-xl p-4">
                    <p className="text-sm font-semibold text-slate-700 flex items-center gap-1.5 mb-2"><Database size={15} className="text-brand" /> Knowledge base <span className="text-xs font-normal text-slate-400">({form.knowledge_count || 0} chunks)</span></p>
                    <div className="flex gap-2">
                      <input value={ingestUrl} onChange={(e) => setIngestUrl(e.target.value)} placeholder="https://site.com/page-to-learn" className={fieldCls} data-testid="ingest-url-input" />
                      <button onClick={runIngest} disabled={busy === "ingest"} data-testid="ingest-btn" className="btn-primary rounded-lg px-4 text-sm whitespace-nowrap flex items-center gap-1.5">{busy === "ingest" ? <Loader2 size={14} className="animate-spin" /> : "Ingest"}</button>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-1.5">Crawls the page + a few internal links, then the agent answers from it.</p>
                  </div>

                  {/* Deploy */}
                  <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
                    <p className="text-sm font-semibold text-slate-700">Deploy</p>
                    <div className="flex items-center gap-2">
                      <a href={assistantLink} target="_blank" rel="noopener noreferrer" className="text-sm text-brand flex items-center gap-1.5" data-testid="assistant-link"><ExternalLink size={14} /> Open assistant page</a>
                    </div>
                    <div className="bg-slate-900 rounded-lg p-3 relative">
                      <button onClick={() => copy(embed)} className="absolute top-2 right-2 text-slate-400 hover:text-white" data-testid="copy-embed"><Copy size={14} /></button>
                      <code className="text-[11px] text-green-300 break-all block pr-6">{embed}</code>
                    </div>
                    <p className="text-[11px] text-slate-400">Paste this before &lt;/body&gt; on any website to embed the assistant.</p>
                  </div>

                  {/* Insights */}
                  {insights && (
                    <div className="grid grid-cols-4 gap-3">
                      <Stat icon={Bot} label="Conversations" value={insights.conversations} />
                      <Stat icon={Users} label="Leads" value={insights.leads_count} />
                      <Stat icon={HelpCircle} label="Knowledge gaps" value={insights.knowledge_gaps?.length || 0} />
                      <Stat icon={ImageIcon} label="Photos" value={insights.photos_count || 0} />
                    </div>
                  )}

                  {/* Photo inbox */}
                  {photos.length > 0 && (
                    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="photo-inbox">
                      <p className="text-sm font-semibold text-slate-700 flex items-center gap-1.5 mb-3"><ImageIcon size={15} className="text-brand" /> Photo inbox <span className="text-xs font-normal text-slate-400">({photos.length} recent)</span></p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {photos.map((p, i) => (
                          <a key={i} href={p.image} target="_blank" rel="noopener noreferrer" data-testid="photo-item" title={p.answer_excerpt} className="block group rounded-lg overflow-hidden border border-slate-200 hover:border-brand transition-colors">
                            <img src={p.image} alt="visitor upload" className="w-full h-24 object-cover" />
                            <div className="p-2">
                              <p className="text-[11px] font-medium text-slate-600 truncate">{p.caption || "Photo only"}</p>
                              <p className="text-[10px] text-slate-400 line-clamp-2">{p.answer_excerpt}</p>
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  {insights?.knowledge_gaps?.length > 0 && (
                    <div className="bg-white border border-slate-200 rounded-xl p-4">
                      <p className="text-sm font-semibold text-slate-700 mb-2">Questions the site couldn't answer</p>
                      <div className="space-y-1">{insights.knowledge_gaps.slice(0, 8).map((g, i) => <div key={i} className="text-sm text-slate-600 flex justify-between"><span className="truncate">{g.question}</span><span className="text-slate-400 shrink-0 ml-2">×{g.count}</span></div>)}</div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4 text-center">
    <Icon size={16} className="text-brand mx-auto" />
    <p className="font-heading text-2xl font-bold text-slate-900 mt-1">{value ?? 0}</p>
    <p className="text-[11px] text-slate-400">{label}</p>
  </div>
);
